<template>
  <v-card elevation="2" rounded="lg">
    <v-card-title class="primary--text pl-4 pr-2">
      <v-icon left color="primary">mdi-cart</v-icon>
      <span class="mr-2">Kauppalista</span>
      <v-chip v-if="simpleMode" small label color="info" outlined class="ml-2"
        >Yksinkertainen</v-chip
      >
      <v-spacer></v-spacer>

      <v-menu offset-y :close-on-content-click="false" left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon v-bind="attrs" v-on="on" title="Asetukset" class="mr-1">
            <v-icon>mdi-cog-outline</v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-subheader>Näkymäasetukset</v-subheader>
          <v-list-item>
            <v-list-item-action class="mr-2">
              <v-switch v-model="simpleMode" color="primary" dense></v-switch>
            </v-list-item-action>
            <v-list-item-title>Yksinkertainen näkymä</v-list-item-title>
          </v-list-item>
          <v-list-item>
            <v-list-item-action class="mr-2">
              <v-switch
                v-model="showPerPersonUsage"
                color="primary"
                dense
              ></v-switch>
            </v-list-item-action>
            <v-list-item-title>Näytä käyttö/hlö</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-btn icon @click="$emit('share')" title="Jaa kauppalista" class="mr-1"
        ><v-icon>mdi-share-variant</v-icon></v-btn
      >
      <v-btn
        icon
        @click="toggleBulkAdd"
        v-if="!simpleMode"
        title="Lisää useita"
        class="mr-1"
        ><v-icon>mdi-playlist-plus</v-icon></v-btn
      >
      <v-btn
        fab
        x-small
        color="primary"
        elevation="2"
        @click="toggleAddItem"
        v-if="!simpleMode"
        title="Lisää tuote"
        ><v-icon>mdi-plus</v-icon></v-btn
      >
    </v-card-title>

    <div
      v-if="shoppingList.length > 0"
      class="d-flex align-center px-4 mb-2 mt-2"
    >
      <div class="flex-grow-1 mr-4">
        <v-progress-linear
          :value="shoppingPercentage"
          height="20"
          rounded
          color="success"
        >
          <template v-slot:default="{ value }">
            <strong class="white--text" style="font-size: 12px"
              >{{ Math.ceil(value) }}% Kerätty</strong
            >
          </template>
        </v-progress-linear>
      </div>
      <div class="text-caption grey--text text-no-wrap">
        {{ shoppingCount }}
      </div>
    </div>

    <v-divider class="mt-0"></v-divider>
    <v-card-text class="pt-4">
      <v-alert
        v-if="!simpleMode"
        border="left"
        colored-border
        color="primary accent-4"
        elevation="0"
        class="grey lighten-5 mb-4"
        dense
      >
        <v-row align="center" no-gutters>
          <v-col class="grow">
            <v-textarea
              :value="publicNotes"
              @input="$emit('update:publicNotes', $event)"
              @change="$emit('save')"
              label="Julkisen kauppalistan muistiinpanot"
              rows="1"
              auto-grow
              dense
              hide-details
              background-color="transparent"
              flat
              solo
              class="pt-0 mt-0"
            ></v-textarea>
          </v-col>
          <v-col cols="auto" class="pl-2">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon small color="grey" v-bind="attrs" v-on="on"
                  >mdi-information-outline</v-icon
                >
              </template>
              <span
                >Tämä kenttä näkyy ja on muokattavissa julkisessa
                kauppalistassa</span
              >
            </v-tooltip>
          </v-col>
        </v-row>
      </v-alert>

      <div v-else-if="publicNotes" class="mb-4 pa-3 grey lighten-4 rounded">
        <div class="text-caption grey--text font-weight-bold mb-1">
          Muistiinpanot:
        </div>
        <div class="subtitle-2">{{ publicNotes }}</div>
      </div>

      <div v-if="templates.length > 0 && !simpleMode" class="mb-4">
        <v-select
          v-model="selectedTemplate"
          label="Tuo kauppalista kauppalistapohjasta"
          :items="templates"
          item-text="name"
          return-object
          dense
          outlined
          hide-details
          class="rounded-lg"
          @change="onTemplateSelect"
          prepend-inner-icon="mdi-file-document-outline"
        ></v-select>
      </div>

      <v-expand-transition>
        <div v-if="showBulkAddForm && !simpleMode" class="mb-4">
          <v-card outlined class="pa-3">
            <div class="text-subtitle-2 mb-2 d-flex align-center">
              <v-icon small class="mr-2">mdi-playlist-plus</v-icon>
              Lisää useita tuotteita
            </div>
            <div class="caption mb-2 grey--text">
              Kirjoita jokainen tuote omalle rivilleen. Tuotteet lisätään
              kategoriaan "Muu" määrällä 1 kpl.
            </div>
            <v-textarea
              v-model="bulkAddText"
              outlined
              dense
              rows="5"
              auto-grow
              :placeholder="'Maito\nKahvi\nSokeri'"
              ref="bulkAddInput"
              hide-details
            ></v-textarea>
            <v-card-actions class="px-0 pb-0 pt-3">
              <v-spacer></v-spacer>
              <v-btn text small @click="showBulkAddForm = false">Peruuta</v-btn>
              <v-btn color="primary" small depressed @click="confirmBulkAdd"
                >Lisää tuotteet</v-btn
              >
            </v-card-actions>
          </v-card>
        </div>
      </v-expand-transition>

      <v-expand-transition>
        <div v-if="showAddItemForm && !simpleMode" class="mb-4">
          <v-card outlined class="pa-3">
            <div class="text-subtitle-2 mb-2 d-flex align-center">
              <v-icon small class="mr-2">mdi-plus-circle-outline</v-icon>
              Lisää uusi tuote
            </div>
            <v-row dense align="center">
              <v-col cols="auto">
                <v-avatar
                  size="40"
                  tile
                  class="mr-2 cursor-pointer rounded"
                  color="grey lighten-3"
                  @click="
                    newItemDraft.imageUrl ? openImage(newItemDraft) : undefined
                  "
                >
                  <img
                    v-if="newItemDraft.imageUrl"
                    :src="newItemDraft.imageUrl"
                  />
                  <v-file-input
                    v-else
                    hide-input
                    prepend-icon="mdi-camera"
                    dense
                    hide-details
                    class="mt-0 pt-0"
                    style="max-width: 30px; margin-left: 6px"
                    accept="image/*"
                    @change="(file) => uploadImage(newItemDraft, file)"
                  ></v-file-input>
                </v-avatar>
              </v-col>
              <v-col>
                <v-text-field
                  v-model="newItemDraft.name"
                  ref="newItemNameInput"
                  dense
                  hide-details
                  placeholder="Tuote"
                  class="pt-0 mt-0 font-weight-bold"
                  @keyup.enter="confirmAddItem"
                  solo
                  flat
                  background-color="grey lighten-4"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row dense class="mt-2">
              <v-col cols="12" sm="5">
                <v-select
                  v-model="newItemDraft.category"
                  :items="categories"
                  dense
                  filled
                  hide-details
                  placeholder="Valitse kategoria..."
                  class="rounded"
                  background-color="grey lighten-4"
                  flat
                  solo
                ></v-select>
              </v-col>
              <v-col cols="6" sm="4">
                <div class="d-flex align-center">
                  <v-text-field
                    v-if="newItemDraft.isPerPerson"
                    v-model.number="newItemDraft.factor"
                    type="number"
                    dense
                    hide-details
                    placeholder="Määrä/hlö"
                    class="pt-0 mt-0 centered-input"
                    solo
                    flat
                    background-color="grey lighten-4"
                  ></v-text-field>
                  <v-text-field
                    v-else
                    v-model.number="newItemDraft.amount"
                    type="number"
                    dense
                    hide-details
                    placeholder="Määrä"
                    class="pt-0 mt-0 centered-input"
                    solo
                    flat
                    background-color="grey lighten-4"
                  ></v-text-field>
                </div>
                <v-checkbox
                  v-model="newItemDraft.isPerPerson"
                  label="Per hlö"
                  dense
                  hide-details
                  class="mt-1 pt-0 caption"
                ></v-checkbox>
              </v-col>
              <v-col cols="6" sm="3">
                <v-text-field
                  v-model="newItemDraft.unit"
                  dense
                  hide-details
                  placeholder="Yksikkö"
                  class="pt-0 mt-0"
                  solo
                  flat
                  background-color="grey lighten-4"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="12">
                <v-text-field
                  v-model="newItemDraft.comment"
                  placeholder="Kommentti (esim. pakkauskoko)"
                  dense
                  hide-details
                  class="pt-0 mt-0 caption"
                  @keyup.enter="confirmAddItem"
                  solo
                  flat
                  background-color="transparent"
                >
                  <template v-slot:prepend-inner>
                    <v-icon small color="grey lighten-1"
                      >mdi-comment-text-outline</v-icon
                    >
                  </template>
                </v-text-field>
              </v-col>
            </v-row>

            <v-card-actions class="px-0 pb-0 pt-2">
              <v-spacer></v-spacer>
              <v-btn text small @click="showAddItemForm = false">Peruuta</v-btn>
              <v-btn color="primary" small depressed @click="confirmAddItem"
                >Lisää</v-btn
              >
            </v-card-actions>
          </v-card>
        </div>
      </v-expand-transition>

      <div v-if="simpleMode">
        <div v-for="(group, category) in groupedShoppingList" :key="category">
          <div class="text-subtitle-2 grey--text mt-2 mb-2 ml-1">
            {{ category }}
          </div>
          <div class="d-flex flex-column">
            <v-card
              v-for="(item, i) in group"
              :key="i"
              outlined
              class="mb-2"
              :class="{ 'grey lighten-5': item.collected }"
            >
              <v-card-text class="pa-2 d-flex align-center">
                <v-checkbox
                  v-model="item.collected"
                  @change="onChange"
                  hide-details
                  class="mt-0 pt-0 mr-3"
                ></v-checkbox>
                <v-avatar
                  v-if="item.imageUrl"
                  size="40"
                  tile
                  class="mr-3 rounded"
                >
                  <v-img :src="item.imageUrl" contain></v-img>
                </v-avatar>
                <div>
                  <div
                    class="subtitle-2"
                    :class="{
                      'text-decoration-line-through grey--text': item.collected,
                    }"
                  >
                    {{ item.name }}
                  </div>
                  <div class="caption grey--text">
                    {{ item.amount }} {{ item.unit }}
                    <span v-if="showPerPersonUsage && headCount > 0">
                      ({{ (item.amount / headCount).toFixed(2) }} / hlö)
                    </span>
                    <span v-if="item.comment"> - {{ item.comment }}</span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </div>
        </div>
      </div>

      <div
        v-else
        v-for="(group, category) in groupedShoppingList"
        :key="category"
      >
        <div class="text-subtitle-2 grey--text mt-2 mb-2 ml-1">
          {{ category }}
        </div>
        <div class="d-flex flex-column">
          <v-card
            v-for="(item, i) in group"
            :key="i"
            outlined
            class="mb-2"
            :class="{ 'grey lighten-5': item.collected }"
          >
            <v-card-text class="pa-2">
              <v-row dense align="center">
                <v-col cols="auto">
                  <v-checkbox
                    v-model="item.collected"
                    @change="onChange"
                    hide-details
                    class="mt-0 pt-0"
                  ></v-checkbox>
                </v-col>

                <v-col cols="auto">
                  <v-avatar
                    size="40"
                    tile
                    class="mr-2 cursor-pointer rounded"
                    color="grey lighten-3"
                    @click="item.imageUrl ? openImage(item) : undefined"
                  >
                    <img v-if="item.imageUrl" :src="item.imageUrl" />
                    <v-file-input
                      v-else
                      hide-input
                      prepend-icon="mdi-camera"
                      dense
                      hide-details
                      class="mt-0 pt-0"
                      style="max-width: 30px; margin-left: 6px"
                      accept="image/*"
                      @change="(file) => uploadImage(item, file)"
                      @click.stop
                    ></v-file-input>
                  </v-avatar>
                </v-col>

                <v-col>
                  <v-text-field
                    v-model="item.name"
                    dense
                    hide-details
                    placeholder="Tuote"
                    class="pt-0 mt-0 font-weight-bold"
                    :class="{
                      'text-decoration-line-through grey--text': item.collected,
                    }"
                    @change="onChange"
                    solo
                    flat
                    background-color="transparent"
                  ></v-text-field>
                </v-col>

                <v-col cols="auto" class="d-flex">
                  <v-menu offset-y left>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on" small>
                        <v-icon>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense>
                      <v-list-item @click="removeItem(item)">
                        <v-list-item-icon
                          ><v-icon color="red"
                            >mdi-delete</v-icon
                          ></v-list-item-icon
                        >
                        <v-list-item-title class="red--text"
                          >Poista</v-list-item-title
                        >
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-col>
              </v-row>

              <v-row dense class="mt-0" align="center">
                <v-col cols="12" sm="5" class="py-0">
                  <v-select
                    v-model="item.category"
                    :items="categories"
                    label="Kategoria"
                    dense
                    hide-details
                    class="caption pt-0"
                    @change="onChange"
                    solo
                    flat
                    background-color="transparent"
                    menu-props="auto"
                  ></v-select>
                </v-col>
                <v-col cols="6" sm="4" class="py-0 d-flex align-center">
                  <v-text-field
                    v-model.number="item.amount"
                    type="number"
                    dense
                    hide-details
                    class="pt-0 centered-input font-weight-medium"
                    style="max-width: 70px"
                    @change="onChange"
                    solo
                    flat
                    background-color="grey lighten-4"
                  ></v-text-field>
                  <v-text-field
                    v-model="item.unit"
                    dense
                    hide-details
                    class="pt-0 ml-1 caption"
                    style="max-width: 60px"
                    @change="onChange"
                    solo
                    flat
                    background-color="transparent"
                  ></v-text-field>
                </v-col>
                <v-col cols="6" sm="4" class="py-0">
                  <div
                    v-if="showPerPersonUsage && headCount > 0"
                    class="text-caption grey--text text-right"
                  >
                    {{ (item.amount / headCount).toFixed(2) }} / hlö
                  </div>
                </v-col>
              </v-row>

              <v-text-field
                v-model="item.comment"
                placeholder="Lisätietoja (esim. pakkauskoko)"
                dense
                hide-details
                class="caption pt-1 px-1"
                @change="onChange"
                solo
                flat
                background-color="transparent"
              >
                <template v-slot:prepend-inner>
                  <v-icon small color="grey lighten-1"
                    >mdi-comment-text-outline</v-icon
                  >
                </template>
              </v-text-field>
            </v-card-text>
          </v-card>
        </div>
      </div>
      <div
        v-if="!shoppingList || shoppingList.length === 0"
        class="text-center grey--text py-4"
      >
        Ei tuotteita
      </div>
    </v-card-text>

    <picture-dialog v-model="selectedPictureUrl">
      <template v-slot:actions>
        <v-btn icon @click="removeImage" title="Poista kuva">
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </template>
    </picture-dialog>
  </v-card>
</template>

<script>
import { groupBy } from "lodash";
import { storage } from "@/plugins/firebase.app";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import PictureDialog from "@/views/PictureDialog.vue";

export default {
  name: "MuonitusShoppingList",
  components: { PictureDialog },
  props: {
    shoppingList: {
      type: Array,
      default: () => [],
    },
    categories: {
      type: Array,
      default: () => [],
    },
    eventId: {
      type: String,
      required: true,
    },
    publicNotes: {
      type: String,
      default: "",
    },
    headCount: {
      type: Number,
      default: 0,
    },
    templates: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      simpleMode: false,
      showPerPersonUsage: false,
      selectedPictureUrl: null,
      selectedItem: null,
      showAddItemForm: false,
      showBulkAddForm: false,
      bulkAddText: "",
      selectedTemplate: null,
      newItemDraft: {
        name: "",
        amount: 1,
        unit: "kpl",
        collected: false,
        fixed: true,
        factor: 1,
        category: "",
        imageUrl: "",
        comment: "",
        isPerPerson: false,
      },
    };
  },
  computed: {
    shoppingPercentage() {
      if (!this.shoppingList || this.shoppingList.length === 0) return 0;
      const collected = this.shoppingList.filter((i) => i.collected).length;
      return (collected / this.shoppingList.length) * 100;
    },
    shoppingCount() {
      if (!this.shoppingList || this.shoppingList.length === 0) return "0/0";
      const collected = this.shoppingList.filter((i) => i.collected).length;
      return `${collected}/${this.shoppingList.length}`;
    },
    groupedShoppingList() {
      const grouped = groupBy(
        this.shoppingList,
        (item) => item.category || "Muu"
      );
      // Sort keys based on categories prop order
      const sortedKeys = Object.keys(grouped).sort((a, b) => {
        const indexA = this.categories.indexOf(a);
        const indexB = this.categories.indexOf(b);
        // If both are in the list, sort by index
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        // If only A is in the list, it comes first
        if (indexA !== -1) return -1;
        // If only B is in the list, it comes first
        if (indexB !== -1) return 1;
        // If neither, sort alphabetically, but put "Muu" last
        if (a === "Muu") return 1;
        if (b === "Muu") return -1;
        return a.localeCompare(b);
      });

      const sortedGrouped = {};
      sortedKeys.forEach((key) => {
        sortedGrouped[key] = grouped[key];
      });
      return sortedGrouped;
    },
  },
  methods: {
    onChange() {
      this.$emit("save");
    },
    onTemplateSelect(template) {
      if (template) {
        this.$emit("apply-template", template);
        this.$nextTick(() => {
          this.selectedTemplate = null;
        });
      }
    },
    toggleBulkAdd() {
      this.showBulkAddForm = !this.showBulkAddForm;
      this.showAddItemForm = false;
      if (this.showBulkAddForm) {
        this.bulkAddText = "";
        this.$nextTick(() => {
          if (this.$refs.bulkAddInput) {
            this.$refs.bulkAddInput.focus();
          }
        });
      }
    },
    confirmBulkAdd() {
      if (!this.bulkAddText) return;
      const lines = this.bulkAddText
        .split("\n")
        .map((l) => l.trim())
        .filter((l) => l);

      if (lines.length === 0) return;

      const newItems = lines.map((line) => ({
        name: line,
        amount: 1,
        unit: "kpl",
        collected: false,
        fixed: true,
        factor: 1,
        category: "Muu",
        imageUrl: "",
        comment: "",
      }));

      this.shoppingList.unshift(...newItems);
      this.onChange();
      this.bulkAddText = "";
      this.showBulkAddForm = false;
      this.$toast(`${newItems.length} tuotetta lisätty`, { type: "success" });
    },
    toggleAddItem() {
      this.showAddItemForm = !this.showAddItemForm;
      this.showBulkAddForm = false;
      if (this.showAddItemForm) {
        this.resetDraft();
        this.$nextTick(() => {
          if (this.$refs.newItemNameInput) {
            this.$refs.newItemNameInput.focus();
          }
        });
      }
    },
    resetDraft() {
      this.newItemDraft = {
        name: "",
        amount: 1,
        unit: "kpl",
        collected: false,
        fixed: true,
        factor: 1,
        category: "",
        imageUrl: "",
        comment: "",
        isPerPerson: false,
      };
    },
    confirmAddItem() {
      if (!this.newItemDraft.name) {
        this.$toast("Anna tuotteelle nimi", { type: "warning" });
        return;
      }
      const itemToAdd = { ...this.newItemDraft };

      if (this.newItemDraft.isPerPerson) {
        itemToAdd.fixed = false;
        itemToAdd.factor = this.newItemDraft.factor;
        itemToAdd.amount = Math.ceil(
          (this.newItemDraft.factor || 0) * (this.headCount || 0)
        );
      } else {
        itemToAdd.fixed = true;
        itemToAdd.factor = this.newItemDraft.amount; // Keep factor in sync just in case
      }
      delete itemToAdd.isPerPerson; // Clean up internal flag

      this.shoppingList.unshift(itemToAdd);
      this.onChange();
      this.resetDraft();
      this.$nextTick(() => {
        if (this.$refs.newItemNameInput) {
          this.$refs.newItemNameInput.focus();
        }
      });
      this.$toast("Tuote lisätty", { type: "success" });
    },
    removeItem(item) {
      const index = this.shoppingList.indexOf(item);
      if (index > -1) {
        this.shoppingList.splice(index, 1);
        this.onChange();
      }
    },
    async shareShoppingList() {
      this.$emit("share");
    },
    openImage(item) {
      this.selectedPictureUrl = item.imageUrl;
      this.selectedItem = item;
    },
    removeImage() {
      if (this.selectedItem) {
        this.selectedItem.imageUrl = "";
        this.onChange();
        this.selectedPictureUrl = null;
        this.selectedItem = null;
      }
    },
    async uploadImage(item, file) {
      if (!file) return;
      try {
        const storageRef = ref(
          storage,
          `shopping-list-images/${Date.now()}_${file.name}`
        );
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        // Vue 2 reactivity caveat
        this.$set(item, "imageUrl", url);
        this.onChange();
        this.$toast("Kuva ladattu", { type: "success" });
      } catch (e) {
        console.error(e);
        this.$toast("Virhe kuvan latauksessa", { type: "error" });
      }
    },
  },
};
</script>

<style scoped>
.centered-input >>> input {
  text-align: center;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
